import { type ReactNode, useEffect, useState } from "react";
import { LogOut, RadioTower, RefreshCcw } from "lucide-react";
import {
  fetchActiveEvents,
  fetchAdminAuditLog,
  fetchAdminAirportConfigs,
  fetchAdminEvents,
  fetchAdminFlightPlans,
  fetchGameState,
  fetchFlightLedgerPreview,
  fetchFlightReleaseDraft,
  createAdminFlightCancellation,
  createAdminFlightOccurrenceEvent,
  createAdminFlightPlan,
  cancelAdminFlightPlan,
  updateAdminAirportConfig,
  logout,
  startAuth,
  verifyAuth,
  type AdminAuditLogRecord,
  type PublicUser
} from "./api";
import {
  createSessionGame,
  createSessionPlayerRotation,
  createSessionCustomFlight,
  deleteSessionAircraft,
  deleteSessionPlayerRotation,
  closeSessionOpsDay,
  redispatchSessionFlight,
  startSessionOpsDay,
  tickSessionOpsDay,
  upsertSessionAircraft,
  type CustomFlightDraft,
  type RotationDraft
} from "./gameSession";
import { draftToEffect, initialGmDraft, type GmDraft, type GmEventInput } from "./gmEvents";
import {
  activateSessionGmEvent,
  cancelSessionGmEvent,
  createSessionGmEvent,
  mergeActiveGmEvent,
  mergeGmEvent,
  type GmEventMutation
} from "./gmSession";
import { Metric } from "./components/Panel";
import { AuthConsole, AccessDenied, CreateAirlinePanel, SystemPanel } from "./features/auth/AuthConsole";
import { PlayerConsole } from "./features/player/PlayerConsole";
import { GmConsole } from "./features/gm/GmConsole";
import { worldData } from "../domain/seed";
import { calculateOperationalKpis, isOpsDayReadyToClose } from "../domain/simulation";
import type { FlightLedgerPreviewOverrides, FlightLedgerResult } from "../domain/simulation";
import type { Aircraft, AirportConfigSet, AirportConfigUpdate, FlightOccurrenceEvent, FlightReleaseDraft, GlobalEvent, RedispatchAction, SaveGame, ScheduleSource, ServiceDay, WorldData } from "../domain/types";


type LoadState = "loading" | "ready" | "unauthenticated" | "error";
type ConsoleMode = "player" | "gm";

const EMPTY_SCHEDULE: ScheduleSource = { flightPlans: [], occurrences: [], cancellations: [], occurrenceEvents: [] };
const EMPTY_AIRPORT_CONFIGS: AirportConfigSet = { ops: [], fuel: [], payload: [], routeMarkets: [] };

function formatLoadState(value: LoadState): string {
  if (value === "ready") return "就绪";
  if (value === "loading") return "连接中";
  if (value === "error") return "错误";
  return "未登录";
}

function currentActiveAdminEvents(events: GlobalEvent[]): GlobalEvent[] {
  const now = Date.now();
  return events.filter((event) => {
    const startsAt = Date.parse(event.startsAt);
    const endsAt = eventEndTimestamp(event.endsAt);
    return event.status === "active" && startsAt <= now && now < endsAt;
  });
}

function adminPermissionMetric(admin: boolean): { value: string; tone: string } {
  if (admin) {
    return { value: "管理员", tone: "green" };
  }
  return { value: "--", tone: "red" };
}

function playerKpiValue(save: SaveGame | null, value: string): string {
  if (save) {
    return value;
  }
  return "--";
}

function cancellationRateMetricTone(cancellationRate: number): string {
  if (cancellationRate > 0) {
    return "red";
  }
  return "amber";
}

function eventEndTimestamp(endsAt: string | undefined): number {
  if (endsAt) {
    return Date.parse(endsAt);
  }
  return Number.POSITIVE_INFINITY;
}

function consoleModeFromPathname(pathname: string): ConsoleMode {
  if (pathname.startsWith("/admin")) {
    return "gm";
  }
  return "player";
}

function loadStatusLine(save: SaveGame | null): string {
  if (save) {
    return "云端存档已同步。";
  }
  return "已登录，等待创建航司。";
}

function unauthenticatedStatusLine(mode: ConsoleMode): string {
  if (mode === "gm") {
    return "GM 席位未登录：请输入管理员邮箱验证码。";
  }
  return "玩家席位未登录：请输入邮箱验证码。";
}

function consoleShellTitle(mode: ConsoleMode): string {
  if (mode === "gm") {
    return "AOC GM 控制台 / EVENT-NET 4.2";
  }
  return "AOC 航司运控席 / OPS-NET 4.2";
}

function operationDayMetric(save: SaveGame | null): string {
  if (!save) {
    return "--";
  }
  if (save.activeOpsDay?.status === "open") {
    return operationDayClockMetric(save.activeOpsDay.day, save.activeOpsDay.currentMinute);
  }
  return `D${save.day}`;
}

function operationDayClockMetric(day: number, currentMinute: number): string {
  const roundedMinute = Math.max(0, Math.round(currentMinute));
  const dayOffset = Math.floor(roundedMinute / 1440);
  const dayLabel = dayOffset > 0 ? `D${day}+${dayOffset}` : `D${day}`;
  return `${dayLabel} ${compactClockTime(roundedMinute)}`;
}

function compactClockTime(currentMinute: number): string {
  const total = currentMinute % 1440;
  const hour = Math.floor(total / 60);
  const minute = total % 60;
  return `${String(hour).padStart(2, "0")}${String(minute).padStart(2, "0")}`;
}

function consoleNavClassName(mode: ConsoleMode, itemMode: ConsoleMode): string {
  if (mode === itemMode) {
    return "active";
  }
  return "";
}

function consoleModeLabel(mode: ConsoleMode): string {
  if (mode === "gm") {
    return "GM 控制台";
  }
  return "玩家控制台";
}

function renderActiveEventStrip(activeEvents: GlobalEvent[]): ReactNode {
  if (activeEvents.length === 0) {
    return <span>无活动全局事件</span>;
  }
  return activeEvents.map((event) => <span key={event.id}>{event.title}</span>);
}

export function App() {
  const mode = consoleModeFromPathname(window.location.pathname);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [user, setUser] = useState<PublicUser | null>(null);
  const [admin, setAdmin] = useState(false);
  const [save, setSave] = useState<SaveGame | null>(null);
  const [world, setWorld] = useState<WorldData>(worldData);
  const [airportConfigs, setAirportConfigs] = useState<AirportConfigSet>(EMPTY_AIRPORT_CONFIGS);
  const [schedule, setSchedule] = useState<ScheduleSource>(EMPTY_SCHEDULE);
  const [activeEvents, setActiveEvents] = useState<GlobalEvent[]>([]);
  const [adminEvents, setAdminEvents] = useState<GlobalEvent[]>([]);
  const [auditLog, setAuditLog] = useState<AdminAuditLogRecord[]>([]);
  const [email, setEmail] = useState("dev@aoc.com");
  const [code, setCode] = useState("9999");
  const [devCode, setDevCode] = useState("9999");
  const [statusLine, setStatusLine] = useState("正在连接云端运控主机...");
  const [customFlightDraft, setCustomFlightDraft] = useState<CustomFlightDraft>({
    kind: "recovery",
    origin: "FRA",
    destination: "PKX",
    aircraftTypeId: "b789",
    scheduledOffsetDays: 0,
    departureTimeLocal: "12:00",
    recoveryForFlightInstanceId: ""
  });
  const [gmDraft, setGmDraft] = useState<GmDraft>(initialGmDraft);

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (loadState !== "ready") return;
    const timer = window.setInterval(() => void refreshActiveEvents(), 30_000);
    return () => window.clearInterval(timer);
  }, [loadState]);

  const latestOperations = save?.operationsLog.slice(0, 8) ?? [];
  const latestKpis = calculateOperationalKpis(latestOperations.filter((operation) => operation.day === (save?.day ?? 0) - 1).flatMap((operation) => operation.legs));
  const dailyProfit = latestOperations.filter((operation) => operation.day === (save?.day ?? 0) - 1).reduce((sum, operation) => sum + operation.profitUsd, 0);
  const openCustomFlights = save?.recoveryFlights.filter((flight) => flight.status === "open" || flight.status === "assigned") ?? [];
  const isAuthenticated = Boolean(user);
  const showConsoleNav = isAuthenticated && admin;
  const adminPermission = adminPermissionMetric(admin);
  const cancellationRateTone = cancellationRateMetricTone(latestKpis.cancellationRate);

  async function load() {
    try {
      const state = await fetchGameState();
      setUser(state.user ?? null);
      setAdmin(Boolean(state.admin));
      setSave(state.save);
      setWorld(state.world);
      setAirportConfigs(state.airportConfigs);
      setSchedule(state.schedule ?? EMPTY_SCHEDULE);
      setActiveEvents(state.activeEvents);
      setLoadState("ready");
      setStatusLine(loadStatusLine(state.save));
      if (mode === "gm" && state.admin) {
        await loadAdminConsoleData();
      }
    } catch (error) {
      const message = errorMessage(error);
      if (message.includes("未登录") || message.includes("登录已过期")) {
        setUser(null);
        setAdmin(false);
        setSave(null);
        setSchedule(EMPTY_SCHEDULE);
        setAirportConfigs(EMPTY_AIRPORT_CONFIGS);
        setActiveEvents([]);
        setLoadState("unauthenticated");
        setStatusLine(unauthenticatedStatusLine(mode));
        return;
      }
      setUser(null);
      setAdmin(false);
      setSave(null);
      setSchedule(EMPTY_SCHEDULE);
      setAirportConfigs(EMPTY_AIRPORT_CONFIGS);
      setActiveEvents([]);
      setAdminEvents([]);
      setAuditLog([]);
      setLoadState("error");
      setStatusLine(`服务器连接失败：${message}`);
    }
  }

  async function loadAdminConsoleData() {
    const [eventsResult, auditResult, scheduleResult, airportResult] = await Promise.all([fetchAdminEvents(), fetchAdminAuditLog(), fetchAdminFlightPlans(), fetchAdminAirportConfigs()]);
    setAdminEvents(eventsResult.events);
    setActiveEvents(currentActiveAdminEvents(eventsResult.events));
    setAuditLog(auditResult.auditLog);
    setSchedule(scheduleResult.schedule);
    setAirportConfigs(airportResult.airportConfigs);
  }

  async function refreshActiveEvents() {
    try {
      if (mode === "gm" && admin) {
        const result = await fetchAdminEvents();
        setAdminEvents(result.events);
        setActiveEvents(currentActiveAdminEvents(result.events));
        return;
      }
      const result = await fetchActiveEvents();
      setActiveEvents(result.events);
    } catch {
      // polling is opportunistic; the next explicit refresh will surface errors.
    }
  }

  async function runAction(action: () => Promise<void>) {
    try {
      await action();
    } catch (error) {
      setStatusLine(`操作失败：${errorMessage(error)}`);
    }
  }

  async function handleStartAuth() {
    const result = await startAuth(email);
    setDevCode(result.devCode ?? "");
    setCode(result.devCode ?? "");
    setStatusLine(result.message);
  }

  async function handleVerifyAuth() {
    const result = await verifyAuth(email, code);
    setLoadState("loading");
    setSave(null);
    setSchedule(EMPTY_SCHEDULE);
    setAirportConfigs(EMPTY_AIRPORT_CONFIGS);
    setActiveEvents([]);
    setUser(result.user);
    setAdmin(result.admin);
    setStatusLine("登录成功，正在读取云端存档。");
    await load();
  }

  async function handleLogout() {
    if (user) {
      await logout();
    }
    setSave(null);
    setUser(null);
    setAdmin(false);
    setAdminEvents([]);
    setAuditLog([]);
    setAirportConfigs(EMPTY_AIRPORT_CONFIGS);
    setLoadState("unauthenticated");
    setStatusLine("已退出当前席位。");
  }

  async function handleCreateGame() {
    const result = await createSessionGame("远东联合航空");
    setSave(result.save);
    if (result.world) {
      setWorld(result.world);
    }
    if (result.airportConfigs) {
      setAirportConfigs(result.airportConfigs);
    }
    if (result.schedule) {
      setSchedule(result.schedule);
    }
    setLoadState("ready");
    setStatusLine("新航司已创建。");
  }

  async function handleStartOpsDay() {
    if (!save) return;
    const result = await startSessionOpsDay();
    setSave(result.save);
    if (result.world) setWorld(result.world);
    if (result.schedule) setSchedule(result.schedule);
    if (result.activeEvents) setActiveEvents(result.activeEvents);
    setStatusLine(`云端运行日已启动：${result.save.activeOpsDay?.flights.length ?? 0} 班追踪中。`);
  }

  async function handleTickOps(minutes: number) {
    if (!save) return;
    const result = await tickSessionOpsDay(minutes);
    if (isOpsDayReadyToClose(result.save)) {
      const closed = await closeSessionOpsDay();
      setSave(closed.save);
      if (closed.world) setWorld(closed.world);
      if (closed.schedule) setSchedule(closed.schedule);
      setActiveEvents(closed.activeEvents);
      setStatusLine(`云端运行日已正常结束：D${result.save.day} -> D${closed.save.day}`);
      return;
    }
    setSave(result.save);
    if (result.world) setWorld(result.world);
    if (result.schedule) setSchedule(result.schedule);
    if (result.activeEvents) setActiveEvents(result.activeEvents);
    setStatusLine(`云端运行时钟推进 ${minutes} 分钟。`);
  }

  async function handleRedispatchFlight(action: RedispatchAction) {
    if (!save) return;
    const result = await redispatchSessionFlight(action);
    setSave(result.save);
    if (result.world) setWorld(result.world);
    if (result.schedule) setSchedule(result.schedule);
    if (result.activeEvents) setActiveEvents(result.activeEvents);
    setStatusLine("云端重签派已写入航班状态流。");
  }

  async function handleFetchFlightReleaseDraft(flightInstanceId: string): Promise<FlightReleaseDraft> {
    const result = await fetchFlightReleaseDraft(flightInstanceId);
    return result.releaseDraft;
  }

  async function handleFetchFlightLedgerPreview(flightInstanceId: string, overrides: FlightLedgerPreviewOverrides = {}): Promise<FlightLedgerResult | undefined> {
    const result = await fetchFlightLedgerPreview(flightInstanceId, overrides);
    return result.ledger;
  }

  async function handleCreateCustomFlight() {
    if (!save) return;
    const result = await createSessionCustomFlight(save, customFlightDraft);
    setSave(result.save);
    setStatusLine(`云端自定义航班已创建：${result.title}`);
  }

  async function handleSavePlayerRotation(input: RotationDraft) {
    if (!save) return;
    const result = await createSessionPlayerRotation({
      id: input.id,
      name: input.name ?? "",
      occurrenceIds: input.occurrenceIds,
      scheduledFlightIds: input.scheduledFlightIds
    });
    setSave(result.save);
    setStatusLine(`云端玩家航班串已保存：${result.name}`);
  }

  async function handleDeletePlayerRotation(rotationId: string) {
    if (!save) return;
    const result = await deleteSessionPlayerRotation(save, rotationId);
    setSave(result.save);
    setStatusLine(`云端玩家航班串已删除：${result.name}`);
  }

  async function handleSaveAircraft(input: Omit<Aircraft, "id"> & { id?: string }) {
    if (!save) return;
    const result = await upsertSessionAircraft(input);
    setSave(result.save);
    setStatusLine(`云端飞机资料已保存：${result.registration}`);
  }

  async function handleDeleteAircraft(aircraftId: string) {
    if (!save) return;
    const result = await deleteSessionAircraft(save, aircraftId);
    setSave(result.save);
    setStatusLine(`云端飞机资料已删除：${result.registration}`);
  }

  async function handleSaveAirportConfig(input: AirportConfigUpdate) {
    const result = await updateAdminAirportConfig(input);
    setAirportConfigs(result.airportConfigs);
    setStatusLine(`机场配置已保存：${input.airportCode}`);
    await loadAdminConsoleData();
  }

  async function handleCreateFlightPlan(input: {
    flightNo: string;
    origin: string;
    destination: string;
    serviceDays: ServiceDay[];
    startsDay: number;
    endsDay?: number;
    departureTimeLocal: string;
    minimumTurnMinutes: number;
    priority: number;
  }) {
    const result = await createAdminFlightPlan(input);
    setSchedule(result.schedule);
    setStatusLine(`GM 点对点航班已下发：${result.flightPlans.length} 个班期 / ${result.occurrences.length} 个具体日期航班`);
  }

  async function handleCancelFlightPlan(flightPlanId: string, reason: string) {
    const result = await cancelAdminFlightPlan(flightPlanId, reason);
    setSchedule(result.schedule);
    setStatusLine(`GM 航班计划已停用：${result.flightPlan.flightNo}`);
  }

  async function handleCancelFlightLeg(input: { occurrenceId: string; day: number; dateIso: string; reason: string }) {
    const result = await createAdminFlightCancellation(input);
    setSchedule(result.schedule);
    setStatusLine(`GM 航段取消已下发：${result.cancellation.occurrenceId} D${result.cancellation.day}`);
  }

  async function handleCreateFlightOccurrenceEvent(input: Omit<FlightOccurrenceEvent, "id" | "status" | "createdBy" | "createdAt">) {
    const result = await createAdminFlightOccurrenceEvent(input);
    setSchedule(result.schedule);
    setStatusLine(`GM 突发航班事件已下发：${result.event.title}`);
  }

  async function handleCreateCustomEvent() {
    await createGmEvent({
      title: gmDraft.title,
      severity: gmDraft.severity,
      scopeType: gmDraft.scopeType,
      scopeValue: gmDraft.scopeValue,
      reason: gmDraft.reason,
      effects: [draftToEffect(gmDraft)],
      activateNow: gmDraft.activateNow,
      durationHours: gmDraft.durationHours
    });
  }

  async function createGmEvent(eventInput: GmEventInput) {
    await applyGmMutation(await createSessionGmEvent(eventInput));
  }

  async function handleActivateEvent(eventId: string) {
    await applyGmMutation(await activateSessionGmEvent(eventId));
  }

  async function handleCancelEvent(eventId: string) {
    await applyGmMutation(await cancelSessionGmEvent(eventId));
  }

  async function applyGmMutation(mutation: GmEventMutation) {
    setAdminEvents((events) => mergeGmEvent(events, mutation.event));
    setActiveEvents((events) => mergeActiveGmEvent(events, mutation.event));
    if (mutation.auditLog) {
      setAuditLog((current) => [mutation.auditLog!, ...current]);
    }
    if (mutation.reloadAdminData) {
      await loadAdminConsoleData();
    }
    setStatusLine(mutation.statusLine);
  }

  const shellTitle = consoleShellTitle(mode);

  function renderMainContent() {
    if (loadState === "loading") {
      return <SystemPanel title="正在连接" body="正在同步云端席位和运控数据。" />;
    }
    if (loadState === "error") {
      return <SystemPanel title="服务器连接失败" body={statusLine} />;
    }
    if (!isAuthenticated) {
      return (
        <AuthConsole
          mode={mode}
          email={email}
          code={code}
          devCode={devCode}
          setEmail={setEmail}
          setCode={setCode}
          onStartAuth={() => void runAction(handleStartAuth)}
          onVerifyAuth={() => void runAction(handleVerifyAuth)}
        />
      );
    }
    if (mode === "gm") {
      if (!admin) {
        return <AccessDenied user={user} />;
      }
      return (
        <GmConsole
          user={user}
          save={save}
          world={world}
          airportConfigs={airportConfigs}
          schedule={schedule}
          activeEvents={activeEvents}
          adminEvents={adminEvents}
          auditLog={auditLog}
          gmDraft={gmDraft}
          setGmDraft={setGmDraft}
          onCreateCustomEvent={() => void runAction(handleCreateCustomEvent)}
          onActivateEvent={(eventId) => void runAction(() => handleActivateEvent(eventId))}
          onCancelEvent={(eventId) => void runAction(() => handleCancelEvent(eventId))}
          onCreateFlightPlan={(input) => void runAction(() => handleCreateFlightPlan(input))}
          onCancelFlightPlan={(flightPlanId, reason) => void runAction(() => handleCancelFlightPlan(flightPlanId, reason))}
          onCancelFlightLeg={(input) => void runAction(() => handleCancelFlightLeg(input))}
          onCreateFlightOccurrenceEvent={(input) => void runAction(() => handleCreateFlightOccurrenceEvent(input))}
          onSaveAircraft={(input) => void runAction(() => handleSaveAircraft(input))}
          onDeleteAircraft={(aircraftId) => void runAction(() => handleDeleteAircraft(aircraftId))}
          onSaveAirportConfig={(input) => void runAction(() => handleSaveAirportConfig(input))}
          onRefresh={() => void runAction(loadAdminConsoleData)}
        />
      );
    }
    if (!save) {
      return <CreateAirlinePanel user={user} onCreateGame={() => void runAction(handleCreateGame)} />;
    }
    return (
      <PlayerConsole
        save={save}
        world={world}
        airportConfigs={airportConfigs}
        schedule={schedule}
        dailyProfit={dailyProfit}
        openCustomFlightCount={openCustomFlights.length}
        activeEvents={activeEvents}
        customFlightDraft={customFlightDraft}
        setCustomFlightDraft={setCustomFlightDraft}
        onCreateGame={() => void runAction(handleCreateGame)}
        onStartOpsDay={() => void runAction(handleStartOpsDay)}
        onTickOps={(minutes) => void runAction(() => handleTickOps(minutes))}
        onRedispatchFlight={(action) => runAction(() => handleRedispatchFlight(action))}
        onFetchFlightReleaseDraft={handleFetchFlightReleaseDraft}
        onFetchFlightLedgerPreview={handleFetchFlightLedgerPreview}
        onCreateCustomFlight={() => void runAction(handleCreateCustomFlight)}
        onSaveRotation={(input) => void runAction(() => handleSavePlayerRotation(input))}
        onDeleteRotation={(rotationId) => void runAction(() => handleDeletePlayerRotation(rotationId))}
      />
    );
  }

  function renderSystemMetrics() {
    if (mode === "gm") {
      return (
        <>
          <Metric label="活动事件" value={String(activeEvents.length)} tone="red"/>
          <Metric label="航班计划" value={String(schedule.flightPlans.filter((plan) => plan.active).length)} tone="amber"/>
          <Metric label="具体航班" value={String(schedule.occurrences.length)} tone="cyan"/>
          <Metric label="权限" value={adminPermission.value} tone={adminPermission.tone}/>
        </>
      );
    }
    return (
      <>
        <Metric label="运营日" value={operationDayMetric(save)} tone="cyan"/>
        <Metric label="正点率" value={playerKpiValue(save, `${latestKpis.arrivalOnTimeRate}%`)} tone="green"/>
        <Metric label="取消率" value={playerKpiValue(save, `${latestKpis.cancellationRate}%`)} tone={cancellationRateTone}/>
        <Metric label="均延误" value={playerKpiValue(save, `${latestKpis.averageArrivalDelayMinutes}m`)} tone="amber"/>
        <Metric label="衔接" value={playerKpiValue(save, String(latestKpis.propagatedConnections))} tone="red"/>
      </>
    );
  }

  return (
    <main className={`aoc-shell ${mode}-shell`}>
      <header className="top-bar">
        <div className="brand-block">
          <RadioTower size={18} />
          <div>
            <strong>{shellTitle}</strong>
            <span>{statusLine}</span>
          </div>
        </div>
        {showConsoleNav && (
          <nav className="console-nav" aria-label="控制台入口">
            <a className={consoleNavClassName(mode, "player")} href="/">
              玩家席位
            </a>
            <a className={consoleNavClassName(mode, "gm")} href="/admin">
              GM 席位
            </a>
          </nav>
        )}
        <div className="system-strip">
          {renderSystemMetrics()}
          <button className="icon-button" onClick={() => void runAction(load)} title="刷新">
            <RefreshCcw size={15} />
          </button>
          {user && (
            <button className="icon-button" onClick={() => void runAction(handleLogout)} title="退出">
              <LogOut size={15} />
            </button>
          )}
        </div>
      </header>

      <section className="alert-strip">
        <span className={`host-state ${loadState}`}>{formatLoadState(loadState)}</span>
        <span>{consoleModeLabel(mode)}</span>
        <span className={statusLineClassName(statusLine)}>{statusLine}</span>
        {renderActiveEventStrip(activeEvents)}
      </section>

      {renderMainContent()}
    </main>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error || "未知错误");
}

function statusLineClassName(statusLine: string): string {
  if (statusLine.startsWith("操作失败")) {
    return "status-line-message error";
  }
  return "status-line-message";
}
